// @flow
import remove from 'lodash/remove';

import { getSearchValue } from '../utils/http';
import { isDevMode } from '../utils/user';
import type { Platform } from '../utils/platform';
import { SMALL } from '../utils';
import type { IUser } from '../modules/account/models/user';
import { isUnlimitedPaidASR } from '../utils/status';
import UserTypes from '../action-types/user';
import BoostTypes from '../action-types/boost';

type AuthenticationError = {
  name: string,
  message: string,
};

type Action =
  | {
      type: 'FETCH_SCRIBE_SESSION_SUCCESS',
      url: string,
      captioner: ScribeCaptioner,
      support: Array<string>,
    }
  | {
      type: 'ROOM_STATUS_UPDATE',
      status: Object,
    }
  | {
      type: 'GET_BRANCH_IO_DATA',
      content: any,
    }
  | {
      type: 'FETCH_AVA_SESSION_SUCCESS',
      url: string,
      captioner: ScribeCaptioner,
      avaId: string,
      roomId: string,
      support: Array<string>,
    }
  | {
      type: 'FETCH_AVA_PROFILE_METRICS',
      metrics: Object,
    }
  | {
      type: 'FETCH_AVA_USER_SUCCESS',
      user: Object,
    }
  | {
      type: 'LOADING_APP',
      status: boolean,
    }
  | {
      type: 'ELECTRON_APP_LOADED',
      value: boolean,
    }
  | { type: 'FETCH_SCRIBE_SESSION_ERROR', error: AuthenticationError }
  | { type: 'CREATE_NEW_CONVERSATION', inConferenceCall: Boolean }
  | { type: 'USER_UPDATE', user: Object }
  | { type: 'TOGGLE_AUDIO_MODE' }
  | { type: 'ENABLE_AUDIO_MODE' }
  | { type: 'ASYNC_BACKEND_ENDPOINT', status: string, endpoints?: Object }
  | { type: 'TOGGLE_DEV_MODE' }
  | { type: 'CLEAR_SCRIBE_SESSION' }
  | { type: 'FETCH_AVA_SESSION_ERROR', error: AuthenticationError }
  | { type: 'TOGGLE_SIDE_BAR', value?: boolean }
  | { type: 'LOST_WEB_SESSION_CONNECTION' }
  | { type: 'CONNECTION_SUCCEEDED' }
  | { type: 'FETCH_BACKEND_SUPPORT', support: Array<string> }
  | { type: 'CLOSE_SCRIBE_SESSION_WINDOW' }
  | { type: 'UPDATE_IS_IN_CONVERSATION', value: boolean }
  | { type: 'SET_AUTHENTICATING', value: boolean }
  | { type: 'MAC_AUDIO_SETUP' }
  | { type: 'SET_URL_TO_OPEN_WITH_CONVO', url: string }
  | { type: 'REDIRECT_TO', to: { pathname: string, search?: string | typeof undefined } }
  | { type: 'CLEAR_REDIRECT' }
  | { type: 'SET_MOUSE_OVER', value: boolean }
  | { type: 'LOGGING_OUT' }
  | { type: 'SET_SHOW_QUICK_SETUP', show: boolean }
  | { type: 'SET_SELECTED_CAPTIONS', value: string }
  | { type: 'RESET_SELECTED_CAPTIONS', paidASRCreditTime: number }
  | { type: 'SET_AUDIO_PARAMS_SENT', value: boolean }
  | { type: 'SET_SHOULD_SHOW_RECONNECTING_BANNER', payload: boolean }
  | { type: 'V1_WS_URL', wsUrl: string }
  | {
      type: 'SET_WEBSOCKET_STATUS',
      payload: 'online' | 'offline' | 'reconnecting',
    }
  | { type: UserTypes.UPDATE_USER_FEATURES_SUCCESS, payload: Object };

export type State = {
  +avaId: string,
  // There are a few user objects because at many different points different people
  // added different ways of handling users from different backends. Eventually
  // it would be beneficial to totally unify them, but that is hard without
  // proper testing. So for now:
  // * please do not fetch the user again. One of the users in the state will be enough for whatever you need.
  // * if you really do need to fetch the user again, please keep the result of that fetch somewhere in the state.

  // This user object is constructed manually in FETCH_AVA_SESSION_SUCCESS
  // action, and then updated on various actions. The complete structure and process
  // that this piece of data undergoes is unknown to the author of this comment.
  +captioner?: ScribeCaptioner,
  // This user object is saved as returned from api.saas.ava.me/v1/users/me.
  // It's mostly used by the 'account' module.
  accountUser?: IUser | typeof undefined,
  // This user object is saved as returned from backend.ava.me/v1/users/me.
  // It's slightly different than accountUser - it has more complete feature
  // flag set, and less complete subscription and payment data. It would be nice
  // if only one of those existed - that piece of backend code is really cool, you should
  // definitely try to unify it!
  avaUser: Object | typeof undefined,
  +currentPlatform: string,
  +error?: AuthenticationError,
  +isInConversation?: boolean,
  +features: FeatureMap,
  +subscription: Subscription,
  +loading?: boolean,
  +electronAppLoaded?: boolean,
  +roomId?: string,
  +socketConnectionStatus: 'PENDING' | 'CONNECTED' | 'DISCONNECTED' | 'CLOSED',
  +url?: string,
  branch: any,
  branchId: string,
  readonly: boolean,
  sidebar: boolean,
  backendSupport: Array<string>,
  intercomDisplayed?: boolean,
  userNameFromUrl?: string,
  lightView: boolean,
  displayable: {
    scribeDashboard: boolean,
    dev: boolean,
    audio: boolean,
    rv: boolean,
  },
  backends: Object,
  asyncBackends: string,
  authenticating: boolean,
  needInternalAudioAccess: boolean,
  microphoneAccess: string,
  showQuickSetup: boolean,
  urlToOpenAfterStartingConvo?: string,
  redirectTo?: { pathname: string, search?: string | typeof undefined } | typeof undefined,
  loggingOut: boolean,
  selectedCaptions: string,
  audioParamsSent: boolean,
  updateUserFeaturesLoading: boolean,
  updateUserNameLoading: boolean,
  updateUserNameSuccess: boolean,
  updateUserHearingProfileLoading: boolean,
  boostWords: string[],
  addBoostWordsLoading: boolean,
  addBoostWordsSuccess: boolean,
  shouldShowReconnectingBanner: boolean,
  websocketStatus: 'online' | 'offline' | 'reconnecting',
};

let needInternalAudioAccess = false;
let microphoneAccess = 'granted';

if (window.isElectron) {
  // We ask electron to check the mac internal audio config
  const audioSetup = window.electronIPC.sendSyncCheckAudioSetup();
  if (audioSetup) {
    needInternalAudioAccess = !audioSetup.internalAudio;
    microphoneAccess = audioSetup.micAccess;
  }
}

const initialState = {
  loading: false,
  electronAppLoaded: false,
  inConferenceCall: false,
  currentPlatform: 'web',
  features: {},
  subscription: {},
  avaId: '',
  socketConnectionStatus: 'PENDING',
  roomId: undefined,
  error: {
    name: '',
    message: '',
  },
  readonly: getSearchValue(window, 'readonly') !== 'false',
  branch: {},
  branchId: localStorage.getItem('ava_branch_id') || '',
  lightView: window.location.href.includes('fii.ava.me/session-'),
  sidebar: false,
  backendSupport: [],
  wsUrl: '',
  intercomDisplayed: window.innerWidth > 800 || microphoneAccess !== 'granted' || needInternalAudioAccess,
  displayable: {
    scribeDashboard: false,
    dev: window.location.hostname === 'localhost',
    audio: true,
    audioDisplay:
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('serveo') ||
      window.location.hostname.includes('avascribe'),
    rv: false,
  },
  userNameFromUrl: getSearchValue(window, 'guestUserName', undefined),
  backends: {
    production: {
      url: 'https://backend.ava.me',
    },
    stage: {
      url: 'https://api-stage.ava.me',
    },
    local: {
      url: 'http://localhost:3000',
    },
  },
  asyncBackends: 'none',
  authenticating: true,
  loggingOut: false,
  needInternalAudioAccess,
  microphoneAccess,
  showQuickSetup: false,
  selectedCaptions: 'free',
  audioParamsSent: false,
  updateUserNameLoading: false,
  updateUserNameSuccess: false,
  updateUserFeaturesLoading: false,
  updateUserHearingProfileLoading: false,
  boostWords: [],
  addBoostWordsLoading: false,
  addBoostWordsSuccess: false,
  shouldShowReconnectingBanner: false,
  websocketStatus: {
    type: 'online',
    background: '#34C54F',
    color: '#fff',
    translation: 'network.nowConnected',
  },
};

const updateIntercomDisplay = (state: State) => ({
  ...state,
  intercomDisplayed:
    !state.features.scribe &&
    (!SMALL || state.microphoneAccess !== 'granted' || state.needInternalAudioAccess) &&
    !state.isInConversation,
});

const updateDisplayable = (state: State) => ({
  ...state,
  displayable: {
    ...state.displayable,
    dev: state.displayable.dev || isDevMode(state.currentPlatform, state.features, state.backends),
    scribeDashboard: state.features.scribe && state.currentPlatform === 'web',
    // eslint-disable-next-line
    rv: state.captioner
      ? state.captioner.userPermissions
        ? state.captioner.userPermissions.includes('scribe-roger-voice')
        : false
      : false,
  },
});

localStorage.removeItem('ava_branch_id');

// $FlowFixMe
export default function scribeConversation(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'FETCH_BACKEND_SUPPORT': {
      // $FlowFixMe
      const { support } = action;

      return updateDisplayable({
        ...state,
        backendSupport: [...new Set(state.backendSupport.concat(support || []))],
      });
    }
    case 'FETCH_SCRIBE_SESSION_ERROR': {
      // $FlowFixMe
      return { ...state, error: action.error };
    }

    case 'LOADING_APP': {
      // $FlowFixMe
      return { ...state, loading: action.status };
    }

    case 'FETCH_SCRIBE_SESSION_SUCCESS': {
      // $FlowFixMe
      const { url, support, captioner } = action;

      return {
        ...state,
        url,
        captioner,
        socketConnectionStatus: 'PENDING',
        error: {
          name: '',
          message: '',
        },
        support: state.backendSupport.concat(support || []),
      };
    }

    case 'UPDATE_IS_IN_CONVERSATION':
      return updateIntercomDisplay({
        ...state,
        // $FlowFixMe
        isInConversation: action.value,
      });

    case 'LOST_WEB_SESSION_CONNECTION': {
      return {
        ...state,
        socketConnectionStatus: 'DISCONNECTED',
      };
    }

    case 'CLOSE_SCRIBE_SESSION_WINDOW': {
      return {
        ...state,
        url: '',
        socketConnectionStatus: 'CLOSED',
      };
    }

    case 'CLEAR_SCRIBE_SESSION': {
      return initialState;
    }

    case 'FETCH_AVA_USER_SUCCESS': {
      // $FlowFixMe
      const {
        user: { features, subscription },
      } = action;
      return updateDisplayable(
        updateIntercomDisplay({
          ...state,
          avaUser: action.user,
          features: features || [],
          subscription: subscription || {},
        })
      );
    }

    case 'TOGGLE_DEV_MODE': {
      return {
        ...state,
        displayable: { ...state.displayable, dev: !state.displayable.dev },
      };
    }

    case 'CONNECTION_SUCCEEDED': {
      return {
        ...state,
        socketConnectionStatus: 'CONNECTED',
      };
    }

    case 'TOGGLE_AUDIO_MODE': {
      return {
        ...state,
        displayable: { ...state.displayable, audio: !state.displayable.audio },
      };
    }

    case 'ENABLE_AUDIO_MODE': {
      return {
        ...state,
        displayable: { ...state.displayable, audio: true },
      };
    }
    case 'FETCH_AVA_SESSION_SUCCESS': {
      // $FlowFixMe
      const { url, captioner, avaId, roomId, support } = action;

      return updateDisplayable({
        ...state,
        socketConnectionStatus: 'CONNECTED',
        url,
        avaId,
        captioner: {
          ...captioner,
          ...(captioner.profile && { profile: captioner.profile }),
          // $FlowFixMe
          id: captioner.firebaseAuthUID || action.captioner.id,
        },
        roomId,
        backendSupport: [...new Set(state.backendSupport.concat(support || []))],
      });
    }

    case 'FETCH_AVA_PROFILE_METRICS': {
      // $FlowFixMe
      const { metrics } = action;

      return updateDisplayable({
        ...state,

        captioner: {
          ...state.captioner,
          profile: {
            ...(state.captioner && state.captioner.profile),
            feedbackMetrics: metrics,
          },
        },
      });
    }

    case 'CREATE_NEW_CONVERSATION': {
      return {
        ...state,
        loading: true,
        // $FlowFixMe
        inConferenceCall: action.inConferenceCall,
      };
    }

    case 'FETCH_AVA_SESSION_ERROR': {
      // $FlowFixMe
      return { ...state, error: action.error };
    }
    case 'TOGGLE_SIDE_BAR': {
      // $FlowFixMe
      if (state.sidebar === action.value) {
        return state;
      }
      return {
        ...state,
        sidebar: !state.sidebar,
      };
    }
    case 'GET_BRANCH_IO_DATA': {
      return {
        ...state,
        // $FlowFixMe
        branch: action.content,
      };
    }
    case 'USER_UPDATE': {
      return {
        ...state,
        captioner: {
          ...state.captioner,
          // $FlowFixMe
          ...action.user,
        },
      };
    }

    case 'ROOM_STATUS_UPDATE': {
      return {
        ...state,
      };
    }

    case 'ASYNC_BACKEND_ENDPOINT': {
      return {
        ...state,
        // $FlowFixMe
        asyncBackends: action.status,
        backends: {
          ...state.backends,
          ...(action.endpoints
            ? // $FlowFixMe
              action.endpoints.reduce((p, c) => ({ ...p, [c.url]: c }), {})
            : {}),
        },
      };
    }

    case 'SET_AUTHENTICATING': {
      return {
        ...state,
        // $FlowFixMe
        authenticating: action.value,
      };
    }

    case 'MAC_AUDIO_SETUP': {
      return {
        ...state,
        microphoneAccess: 'granted',
        needInternalAudioAccess: false,
        showQuickSetup: false,
      };
    }

    case 'SET_URL_TO_OPEN_WITH_CONVO': {
      return {
        ...state,
        // $FlowFixMe
        urlToOpenAfterStartingConvo: action.url,
      };
    }

    case 'REDIRECT_TO': {
      return {
        ...state,
        // $FlowFixMe
        redirectTo: action.to,
      };
    }

    case 'CLEAR_REDIRECT': {
      return {
        ...state,
        redirectTo: undefined,
      };
    }

    case 'LOGGING_OUT': {
      return {
        ...state,
        loggingOut: true,
      };
    }

    case 'SET_SHOW_QUICK_SETUP': {
      return {
        ...state,
        // $FlowFixMe
        showQuickSetup: action.show,
      };
    }
    case 'SET_SELECTED_CAPTIONS': {
      return {
        ...state,
        // $FlowFixMe
        selectedCaptions: action.value,
      };
    }
    case 'RESET_SELECTED_CAPTIONS': {
      const { captioner: user, subscription } = state;
      // $FlowFixMe
      const asrTime = Math.floor(action.paidASRCreditTime / 1000);

      const unlimitedPaidASR = isUnlimitedPaidASR(user, subscription);
      const canSelectPremium = unlimitedPaidASR || asrTime > 0;

      return {
        ...state,
        selectedCaptions: canSelectPremium ? 'premium' : 'free',
      };
    }
    case 'V1_WS_URL': {
      return {
        ...state,
        wsUrl: action.wsUrl || '',
      };
    }

    case 'SET_AUDIO_PARAMS_SENT': {
      return {
        ...state,
        // $FlowFixMe
        audioParamsSent: action.value,
      };
    }

    case 'ELECTRON_APP_LOADED': {
      return {
        ...state,
        // $FlowFixMe
        electronAppLoaded: action.value,
      };
    }

    case 'SET_SHOULD_SHOW_RECONNECTING_BANNER': {
      return {
        ...state,
        shouldShowReconnectingBanner: action.payload,
      };
    }

    case 'SET_WEBSOCKET_STATUS': {
      return {
        ...state,
        websocketStatus: action.payload,
      };
    }

    case UserTypes.UPDATE_USER_FEATURES_REQUEST:
      return {
        ...state,
        updateUserFeaturesLoading: true,
      };

    case UserTypes.UPDATE_USER_FEATURES_SUCCESS:
      return {
        ...state,
        captioner: {
          ...state.captioner,
          features: {
            // $FlowFixMe
            ...state.captioner.features,
            ...action.payload,
          },
        },
        updateUserFeaturesLoading: false,
      };

    case UserTypes.UPDATE_USER_FEATURES_FAILURE:
      return {
        ...state,
        updateUserFeaturesLoading: false,
      };

    case UserTypes.UPDATE_USER_HEARING_PROFILE_REQUEST:
      return {
        ...state,
        updateUserHearingProfileLoading: true,
      };

    case UserTypes.UPDATE_USER_HEARING_PROFILE_SUCCESS:
      return {
        ...state,
        captioner: {
          ...state.captioner,
          profile: {
            // $FlowFixMe
            ...state.captioner.profile,
            hearingProfile: action.payload,
          },
        },
        updateUserHearingProfileLoading: false,
      };

    case UserTypes.UPDATE_USER_HEARING_PROFILE_FAILURE:
      return {
        ...state,
        updateUserHearingProfileLoading: false,
      };

    case UserTypes.UPDATE_USER_NAME_REQUEST:
      return {
        ...state,
        updateUserNameLoading: true,
        updateUserNameSuccess: false,
      };

    case UserTypes.UPDATE_USER_NAME_SUCCESS:
      return {
        ...state,
        captioner: {
          ...state.captioner,
          userName: action.payload,
        },
        updateUserNameLoading: false,
        updateUserNameSuccess: true,
      };

    case UserTypes.UPDATE_USER_NAME_FAILURE:
      return {
        ...state,
        updateUserNameLoading: false,
      };

    case UserTypes.SET_UPDATE_USER_NAME_SUCCESS:
      return {
        ...state,
        updateUserNameSuccess: action.payload,
      };

    case UserTypes.SET_ORGANIZATION:
      return {
        ...state,
        captioner: {
          ...state.captioner,
          organization: action.payload,
        },
      };

    case BoostTypes.ADD_BOOST_WORDS_REQUEST:
      return {
        ...state,
        addBoostWordsLoading: true,
      };

    case BoostTypes.SET_ADD_BOOST_WORDS_SUCCESS:
      return {
        ...state,
        addBoostWordsSuccess: action.payload,
      };

    case BoostTypes.SET_ADD_BOOST_WORDS_LOADING:
      return {
        ...state,
        addBoostWordsLoading: action.payload,
      };

    case BoostTypes.SET_BOOST_WORDS:
      let newState = {};
      if (state.addBoostWordsLoading) {
        newState = {
          addBoostWordsLoading: false,
          addBoostWordsSuccess: true,
        };
      }

      // $FlowFixMe
      const words = action.payload ? action.payload.split(',').map(w => w.trim()) : [];
      return {
        ...state,
        ...newState,
        boostWords: words,
      };

    default:
      // $FlowFixMe
      (action: empty); // eslint-disable-line no-unused-expressions
      return state;
  }
}
